import React, { useCallback, useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { motion, useScroll, useSpring } from "framer-motion";

const TabWrap = styled.div.attrs({ id: "tabBusiness" })`
    ${tw`sticky top-below-header md:top-below-header-md z-40 mx-auto w-full whitespace-nowrap border-b border-gray-300 bg-white text-gray-800`}
`;

const TabScroll = styled.div`
    ${tw`flex overflow-x-scroll overflow-y-hidden`}
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }

    @media (prefers-reduced-motion: no-preference) {
        scroll-behavior: smooth;
    }
`;

const TabTrack = styled.div`
    ${tw`mx-auto flex w-max items-center`}
`;

const Fade = styled.div<{ $side: "left" | "right" }>`
    ${tw`pointer-events-none absolute top-0 bottom-0 w-10`}
    ${({ $side }) => ($side === "left" ? tw`left-0` : tw`right-0`)}
    background: linear-gradient(
        to ${({ $side }) => ($side === "left" ? "right" : "left")},
        #fff,
        rgba(255, 255, 255, 0)
    );
`;

const ProgressBar = styled(motion.div)`
    transform-origin: 0%;
    ${tw`absolute -bottom-px left-0 h-1 w-full bg-brand`}
`;

const TABS = [
    { key: "dt", href: "/dt", label: "Salesforce" },
    { key: "agent", href: "/agent", label: "Agentforce" },
    { key: "databricks", href: "/databricks", label: "Databricks" },
    { key: "snowflake", href: "/snowflake", label: "Snowflake" },
    { key: "dreamfactory", href: "/dreamfactory", label: "Dreamfactory" },
    { key: "integrate", href: "/integrate", label: "Integrate" },
    { key: "ICT", href: "/ICT", label: "ICT융합기술R&D / 사업화" },
    {
        key: "service-incubation",
        href: "/service-incubation",
        label: "서비스 인큐베이션"
    }
] as const;

export type BusinessTabKey = (typeof TABS)[number]["key"];

const BusinessTabs = ({ current }: { current: BusinessTabKey }) => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    const scrollRef = useRef<HTMLDivElement>(null);
    const activeRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        const box = scrollRef.current;
        const tab = activeRef.current;
        if (!box || !tab) return;

        const center = (behavior: "instant" | "smooth") => {
            const boxRect = box.getBoundingClientRect();
            const tabRect = tab.getBoundingClientRect();
            const tabStart = tabRect.left - boxRect.left + box.scrollLeft;
            box.scrollTo({
                left: tabStart + tabRect.width / 2 - box.clientWidth / 2,
                // instant 는 이 TS 버전의 ScrollBehavior 에 없어 캐스트한다
                behavior: behavior as ScrollBehavior
            });
        };

        center("instant");
        document.fonts?.ready.then(() => center("smooth"));
    }, [current]);

    const [more, setMore] = useState({ left: false, right: false });

    const syncEdges = useCallback(() => {
        const box = scrollRef.current;
        if (!box) return;
        const max = box.scrollWidth - box.clientWidth;
        setMore({ left: box.scrollLeft > 1, right: box.scrollLeft < max - 1 });
    }, []);

    useEffect(() => {
        const box = scrollRef.current;
        if (!box) return;
        syncEdges();
        box.addEventListener("scroll", syncEdges, { passive: true });
        const ro = new ResizeObserver(syncEdges);
        ro.observe(box);
        return () => {
            box.removeEventListener("scroll", syncEdges);
            ro.disconnect();
        };
    }, [syncEdges]);

    useEffect(() => {
        const box = scrollRef.current;
        if (!box) return;

        const onWheel = (e: WheelEvent) => {
            const max = box.scrollWidth - box.clientWidth;
            if (max <= 0) return;
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
            const atStart = e.deltaY < 0 && box.scrollLeft <= 0;
            const atEnd = e.deltaY > 0 && box.scrollLeft >= max;
            if (atStart || atEnd) return;

            e.preventDefault();
            // smooth 를 타면 휠 한 칸마다 애니메이션이 걸려 끊긴다
            box.scrollBy({
                left: e.deltaY,
                behavior: "instant" as ScrollBehavior
            });
        };

        box.addEventListener("wheel", onWheel, { passive: false });
        return () => box.removeEventListener("wheel", onWheel);
    }, []);

    return (
        <TabWrap>
            <TabScroll ref={scrollRef}>
                <TabTrack>
                    {TABS.map(tab => {
                        const active = tab.key === current;
                        return (
                            <a
                                key={tab.key}
                                ref={active ? activeRef : undefined}
                                href={active ? undefined : tab.href}
                                aria-current={active ? "page" : undefined}
                                className={`px-5 py-4 ${
                                    active ? "font-extrabold text-brand" : ""
                                }`}
                            >
                                {tab.label}
                            </a>
                        );
                    })}
                </TabTrack>
            </TabScroll>

            {more.left && <Fade $side="left" aria-hidden="true" />}
            {more.right && <Fade $side="right" aria-hidden="true" />}

            <ProgressBar style={{ scaleX }} />
        </TabWrap>
    );
};

export default BusinessTabs;
